import { useEffect, useId, useRef } from 'react';
import type { ReactNode } from 'react';
import { clsx } from 'clsx';
import { XIcon } from './icons.tsx';

/**
 * A dialog for forms, where `ConfirmDialog` is for questions.
 *
 * Both are here for the same reason: the native equivalents cannot be styled,
 * cannot be made to match the app's dark mode, and block the renderer. The two
 * are kept separate because a confirm resolves a promise and a form does not.
 */

const SIZES = {
  sm: 'max-w-sm',
  md: 'max-w-xl',
  lg: 'max-w-3xl',
};

interface ModalProps {
  open: boolean;
  title: string;
  description?: string;
  size?: keyof typeof SIZES;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}

export function Modal({
  open,
  title,
  description,
  size = 'md',
  onClose,
  children,
  footer,
}: ModalProps): React.JSX.Element | null {
  const panelRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;

    previousFocusRef.current = document.activeElement as HTMLElement | null;

    // The first control, not the panel: a form dialog that opens with the
    // cursor already in its first input saves a click every single time.
    const focusable = panelRef.current?.querySelector<HTMLElement>(
      'input:not([type=hidden]), textarea, select, button, [tabindex]:not([tabindex="-1"])',
    );
    (focusable ?? panelRef.current)?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !panelRef.current) return;

      const list = [
        ...panelRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]):not([type=hidden]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ].filter((element) => element.offsetParent !== null);

      if (list.length === 0) return;

      const index = list.indexOf(document.activeElement as HTMLElement);
      const next = event.shiftKey ? index - 1 : index + 1;

      // Only intercept at the ends; inside the list the browser does it better
      // (it knows about shadow roots and content-editable, we do not).
      if (next >= 0 && next < list.length) return;

      event.preventDefault();
      list[(next + list.length) % list.length]?.focus();
    };

    document.addEventListener('keydown', onKeyDown);

    // The page behind must not scroll while a dialog is over it, or a phone
    // scrolls the list instead of the form.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      previousFocusRef.current?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div
        className="fixed inset-0 bg-slate-900/50 backdrop-blur-[1px]"
        aria-hidden="true"
        onClick={onClose}
      />

      <div
        ref={panelRef}
        tabIndex={-1}
        className={clsx(
          'relative my-auto w-full rounded-xl border border-slate-200 bg-white shadow-xl outline-none dark:border-slate-700 dark:bg-slate-900',
          SIZES[size],
        )}
      >
        <div className="flex items-start gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-800">
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="text-sm font-semibold text-slate-900 dark:text-slate-50">
              {title}
            </h2>
            {description ? (
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{description}</p>
            ) : null}
          </div>

          <button type="button" className="lg-btn-ghost px-2" onClick={onClose}>
            <XIcon className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        </div>

        <div className="lg-scroll max-h-[70vh] overflow-y-auto px-5 py-4">{children}</div>

        {footer ? (
          <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4 dark:border-slate-800">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}
